/**
 * Knowledge-base training session, full duplex (owner decision 2026-09-25).
 *
 * The AI customer reads the track's KB items out one after another, in an
 * order and at a pace the SERVER decides; the voice model only takes turns and
 * speaks. This page streams the microphone, plays the customer, and shows
 * what the server says ON THE LIVE-CALL SCREEN (TrainingCallLayout): the
 * transcript in the top bar, each item's objection/FAQ entry in the
 * conversation feed or its landmine/competitor alert, the track's playbook,
 * and in the status panel "N of M answered", the current item and a gentle
 * "Still there?" while the server checks in on a silent rep.
 *
 * Deliberately absent: any silence gate, end-of-turn timer, watchdog or submit
 * button. Those belonged to the turn-based engine this replaced.
 *
 * TrainingSessionRouter sends knowledge-base tracks here and playbook tracks
 * to PlaybookLiveSessionPage.
 */
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';

import KbCompletionModal from '../components/kbLive/KbCompletionModal';
import { openTrainingResults } from '../utils/trainingResults';
import KbCurrentItem from '../components/kbLive/KbCurrentItem';
import KbSilenceBanner from '../components/kbLive/KbSilenceBanner';
import TrainingCallLayout from '../components/training/TrainingCallLayout';
import TrainingStatusPanel from '../components/training/TrainingStatusPanel';
import { AlertModal } from '../components/ui/AlertModal';
import { KB_LIVE_COPY } from '../constants/training';
import { useKbLiveSession } from '../hooks/useKbLiveSession';
import { useTrainingSessionEnded } from '../hooks/useTrainingSessionEnded';
import { useTrainingTrackPlaybook } from '../hooks/useTrainingTrackPlaybook';
import type { RootState } from '../store';
import { logoutUser } from '../store/authSlice';
import { formatKbProgressLabel } from '../utils/kbItemProgress';

const TrainingSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const trackId = new URLSearchParams(location.search).get('trackId');

  const { isAuthenticated, tokenInfo, isMongoVerified } = useSelector((state: RootState) => state.auth);
  const isSignedIn = Boolean(isAuthenticated && tokenInfo?.token && isMongoVerified);

  const session = useKbLiveSession(trackId, sessionId);
  const { phase, state, teardown } = session;
  const hasPlaybook = useTrainingTrackPlaybook(isSignedIn ? trackId : null);
  const hasSessionEnded = useTrainingSessionEnded(sessionId, isSignedIn);

  useEffect(() => {
    if (!isSignedIn) navigate('/login');
  }, [isSignedIn, navigate]);

  const handleSignOut = useCallback(async () => {
    // Close the session first: signing out mid-call would leave the mic open.
    await teardown();
    try {
      await signOut(getAuth());
    } catch (signOutError) {
      console.warn('TrainingSessionPage: sign-out failed, continuing to login', signOutError);
    }
    dispatch(logoutUser() as never);
    navigate('/login');
  }, [dispatch, navigate, teardown]);

  const handleViewResults = useCallback(() => openTrainingResults(), []);

  if (!isSignedIn) return null;

  const isInSession = phase === 'live' || phase === 'completed';

  const statusPanel = (
    <TrainingStatusPanel
      phase={phase}
      onStart={session.start}
      onEnd={session.end}
      isEnding={session.isEnding}
      turn={session.turn}
      progressLabel={isInSession ? formatKbProgressLabel(state.progress.itemsAnswered, state.totalItems) : undefined}
    >
      {isInSession && <KbCurrentItem item={state.currentItem} />}
      {phase === 'live' && state.isCheckingIn && <KbSilenceBanner />}
    </TrainingStatusPanel>
  );

  return (
    <TrainingCallLayout
      onSignOut={handleSignOut}
      statusPanel={statusPanel}
      showPlaybook={hasPlaybook}
      isSessionActive={phase === 'live'}
    >
      <AlertModal isOpen={Boolean(session.error)} message={session.error ?? ''} onClose={session.clearError} />
      <AlertModal isOpen={hasSessionEnded} message={KB_LIVE_COPY.alreadyEnded} onClose={handleSignOut} />
      {phase === 'completed' && state.summary && (
        <KbCompletionModal summary={state.summary} onViewResults={handleViewResults} />
      )}
    </TrainingCallLayout>
  );
};

export default TrainingSessionPage;
